import React, { useState, useEffect, useRef } from "react";
import { Route, Redirect } from "react-router-dom";
import { getPublicUrl } from "../utils/Utils";
import http from "../utils/http-common";
import swal from "sweetalert";
import Content from "../layout/content/Content";
import Head from "../layout/head/Head";
import { Block, BlockHead, BlockHeadContent, BlockTitle, BlockDes } from "../components/Component";
import { userHasAdminAccess } from "../utils/adminAccess";

/**
 * AdminRoute - Production-ready route protection component
 * 
 * 🎯 Objectif : Protéger les routes admin en vérifiant l'authentification et le rôle admin
 * 
 * 💡 Décisions techniques :
 * - useRef pour isMountedRef : prévient les setState après démontage (memory leak)
 * - useRef pour timeoutIds : nettoie tous les setTimeout (memory leak)
 * - Vérification isMounted avant chaque setState : évite les warnings React
 * - Fallback direct si swal échoue : garantit la redirection même en cas d'erreur
 * - Timeout API avec AbortController : évite les requêtes qui traînent
 * - Cleanup complet dans useEffect : prévient toutes les fuites mémoire
 * 
 * ⚠️ Trade-offs assumés :
 * - Pas de retry automatique : l'utilisateur doit recharger manuellement (évite les boucles)
 * - swal avec setTimeout : nécessaire pour éviter de bloquer le render, mais nettoyé proprement
 * - Redirection externe avec window.location.replace : évite l'historique mais ne peut pas être annulée
 * 
 * 🔍 À surveiller en prod :
 * - Logs des erreurs 401/403 pour détecter les tentatives d'accès non autorisées
 * - Temps de réponse de /api/v1/user/me (doit être < 1s)
 * - Taux d'erreurs réseau (timeout, 500, etc.)
 * - Memory leaks dans DevTools (Performance tab)
 */
const AdminRoute = ({ exact, component: Component, render, ...rest }) => {
  const [isAuthorized, setIsAuthorized] = useState(null); // null = loading, true = authorized, false = unauthorized
  const [isLoading, setIsLoading] = useState(true);
  const [redirectTo, setRedirectTo] = useState(null); // For internal redirects
  const isCheckingRef = useRef(false); // Prevent multiple simultaneous checks
  const hasRedirectedRef = useRef(false); // Prevent multiple redirects
  const isMountedRef = useRef(true); // Track if component is mounted (prevent memory leaks)
  const timeoutIdsRef = useRef([]); // Track all timeouts for cleanup
  const abortControllerRef = useRef(null); // Track API request for cancellation

  // Safe setState wrapper: only update if component is still mounted
  const safeSetState = (setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    } else if (process.env.NODE_ENV === 'development') {
      console.warn('[AdminRoute] Attempted to setState after unmount');
    }
  };

  // Safe redirect wrapper: ensures redirect happens even if swal fails
  const safeRedirect = (url, isExternal = false, message = null) => {
    if (hasRedirectedRef.current) {
      return;
    }
    hasRedirectedRef.current = true;

    const performRedirect = () => {
      if (isExternal) {
        window.location.replace(url);
      } else {
        safeSetState(setRedirectTo, url);
      }
    };

    // Try to show message, but always redirect even if swal fails
    if (message) {
      try {
        const timeoutId = setTimeout(() => {
          swal(message.title, message.text, message.type || "error")
            .then(() => {
              performRedirect();
            })
            .catch(() => {
              // Fallback: redirect even if swal fails
              performRedirect();
            });
        }, 100);
        timeoutIdsRef.current.push(timeoutId);
      } catch (error) {
        // Fallback: redirect immediately if swal is not available
        console.warn("[AdminRoute] swal failed, redirecting anyway:", error);
        performRedirect();
      }
    } else {
      performRedirect();
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    abortControllerRef.current = new AbortController();

    const checkAdminAccess = async () => {
      // Prevent multiple simultaneous checks
      if (isCheckingRef.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[AdminRoute] Check already in progress, skipping');
        }
        return;
      }

      // Prevent check if already redirected
      if (hasRedirectedRef.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[AdminRoute] Already redirected, skipping check');
        }
        return;
      }

      // Prevent check if component is unmounted
      if (!isMountedRef.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[AdminRoute] Component unmounted, skipping check');
        }
        return;
      }

      isCheckingRef.current = true;
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[AdminRoute] Starting admin access check');
      }

      try {
        // Check if token exists
        const token = localStorage.getItem("accessToken");
        if (!token) {
          safeSetState(setIsAuthorized, false);
          safeSetState(setIsLoading, false);
          isCheckingRef.current = false;
          safeRedirect(getPublicUrl("auth-login"), false);
          return;
        }

        // Fetch user info to check role with timeout
        // Create timeout to abort request after 10 seconds
        const apiTimeoutId = setTimeout(() => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
        }, 10000);
        timeoutIdsRef.current.push(apiTimeoutId);
        
        const response = await http.get("/api/v1/user/me", {
          signal: abortControllerRef.current.signal,
          suppressServerErrorSwal: true,
        });
        
        // Clear timeout if request completes before timeout
        clearTimeout(apiTimeoutId);
        timeoutIdsRef.current = timeoutIdsRef.current.filter(id => id !== apiTimeoutId);
        
        // Check if component is still mounted before processing response
        if (!isMountedRef.current) {
          return;
        }

        // Defensive check: validate response structure
        if (!response || !response.data) {
          if (process.env.NODE_ENV === 'development') {
            console.error('[AdminRoute] Invalid response structure:', response);
          }
          safeSetState(setIsAuthorized, false);
          safeSetState(setIsLoading, false);
          isCheckingRef.current = false;
          safeRedirect(getPublicUrl("auth-login"), false);
          return;
        }

        // Handle different response structures
        const user = response.data?.data || response.data;
        
        // Debug log (always log in production for troubleshooting)
        console.log('[AdminRoute] User data received:', {
          hasData: !!user,
          userRole: user?.role,
          userEmail: user?.email,
          responseStructure: response.data?.data ? 'nested' : 'flat',
          fullUser: user
        });

        // Defensive check: user must exist and be an object
        if (!user || typeof user !== 'object') {
          safeSetState(setIsAuthorized, false);
          safeSetState(setIsLoading, false);
          isCheckingRef.current = false;
          safeRedirect(getPublicUrl("auth-login"), false);
          return;
        }

        // Defensive role check: align with API RBAC (hasRole), not only primary role string
        const userRole = user.role;
        const normalizedRole = userRole ? String(userRole).trim().toLowerCase() : '';
        const isAdmin = userHasAdminAccess(user);

        // Debug log (always log in production for troubleshooting)
        console.log('[AdminRoute] User role check:', {
          rawRole: userRole,
          normalizedRole: normalizedRole,
          secondaryRoles: user.secondaryRoles,
          isAdmin: isAdmin,
          userEmail: user.email,
          fullUserObject: user
        });

        if (isAdmin) {
          try {
            localStorage.setItem("user", JSON.stringify(user));
          } catch (_) {}
          safeSetState(setIsAuthorized, true);
          safeSetState(setIsLoading, false);
          isCheckingRef.current = false; // Reset checking flag
        } else {
          // User is not admin - redirect to login with explanation
          safeSetState(setIsAuthorized, false);
          safeSetState(setIsLoading, false);
          isCheckingRef.current = false; // Reset checking flag
          safeRedirect(
            getPublicUrl("auth-login"),
            false,
            {
              title: "Access denied",
              text: "Sign in with an administrator account.",
              type: "error"
            }
          );
        }
      } catch (error) {
        // Don't process errors if component is unmounted
        if (!isMountedRef.current) {
          return;
        }

        // Handle AbortError (component unmounted or timeout)
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          // Component was unmounted or request was cancelled, don't update state
          return;
        }

        const status = error.response?.status;

        // Handle 401: Unauthorized (token invalid/expired)
        if (status === 401) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("user");
          safeSetState(setIsAuthorized, false);
          safeSetState(setIsLoading, false);
          safeRedirect(
            getPublicUrl("auth-login"),
            false,
            {
              title: "Session expired",
              text: "Please sign in again.",
              type: "error"
            }
          );
        } 
        // Handle 403: Forbidden (user exists but doesn't have permission)
        else if (status === 403) {
          safeSetState(setIsAuthorized, false);
          safeSetState(setIsLoading, false);
          safeRedirect(
            getPublicUrl("auth-login"),
            false,
            {
              title: "Access denied",
              text: "Sign in with an administrator account.",
              type: "error"
            }
          );
        }
        // Handle network errors, timeouts, etc.
        else if (!error.response) {
          safeSetState(setIsAuthorized, false);
          safeSetState(setIsLoading, false);
          const timeoutId = setTimeout(() => {
            if (isMountedRef.current) {
              swal(
                "Connection error",
                "Could not reach the server. Check your network connection.",
                "error"
              ).catch((err) => {
                console.warn("[AdminRoute] swal failed:", err);
              });
            }
          }, 100);
          timeoutIdsRef.current.push(timeoutId);
        }
        // Server errors (intercepteur désactivé sur cette requête — un seul message ici)
        else if (status >= 500) {
          safeSetState(setIsAuthorized, false);
          safeSetState(setIsLoading, false);
          const rid =
            error.response?.headers?.["x-request-id"] ||
            error.response?.headers?.["X-Request-Id"] ||
            error.response?.data?.requestId;
          const apiErr = error.response?.data?.error;
          const devStack =
            process.env.NODE_ENV === "development" && error.response?.data?.stack
              ? `\n\n${String(error.response.data.stack).split("\n").slice(0, 4).join("\n")}`
              : "";
          const detail = rid ? ` Reference: ${rid}` : "";
          const extra =
            apiErr && String(apiErr).trim() && apiErr !== "Internal server error"
              ? `\n\n${apiErr}`
              : "";
          const timeoutId = setTimeout(() => {
            if (isMountedRef.current) {
              swal(
                "Server error",
                `The server could not verify your session.${detail}${extra}${devStack}`,
                "error"
              ).catch((err) => {
                console.warn("[AdminRoute] swal failed:", err);
              });
            }
          }, 100);
          timeoutIdsRef.current.push(timeoutId);
        }
        // Autres erreurs HTTP (404, etc.)
        else {
          safeSetState(setIsAuthorized, false);
          safeSetState(setIsLoading, false);
          const timeoutId = setTimeout(() => {
            if (isMountedRef.current) {
              swal(
                "Server error",
                "Something went wrong while checking your permissions. Please try again.",
                "error"
              ).catch((err) => {
                console.warn("[AdminRoute] swal failed:", err);
              });
            }
          }, 100);
          timeoutIdsRef.current.push(timeoutId);
        }
      } finally {
        isCheckingRef.current = false;
      }
    };

    checkAdminAccess();

    // Listen for storage changes (logout from other tabs)
    // Only listen if not already checking or redirected
    const handleStorageChange = (e) => {
      if (hasRedirectedRef.current || isCheckingRef.current || !isMountedRef.current) {
        return;
      }
      
      if (e.key === 'accessToken') {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          safeSetState(setIsAuthorized, false);
          safeRedirect(getPublicUrl("auth-login"), false);
        }
        // Don't re-check on storage change to avoid loops
        // The initial check on mount is sufficient
      }
    };

    // Listen for custom auth events (logout from other tabs or token removal)
    const handleCustomStorageChange = () => {
      // Ignore if already redirected, checking, or unmounted
      if (hasRedirectedRef.current || isCheckingRef.current || !isMountedRef.current) {
        return;
      }
      
      // Only handle logout events (token removal), not login events
      // Login events are handled by page navigation/reload
      const token = localStorage.getItem("accessToken");
      if (!token) {
        // Token was removed - user logged out
        safeSetState(setIsAuthorized, false);
        safeSetState(setIsLoading, false);
        safeRedirect(getPublicUrl("auth-login"), false);
      }
      // If token exists, don't do anything - let the initial mount check handle it
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authChange', handleCustomStorageChange);

    // Cleanup function: prevent memory leaks
    return () => {
      isMountedRef.current = false;
      
      // Cancel ongoing API request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Clear all timeouts
      timeoutIdsRef.current.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      timeoutIdsRef.current = [];
      
      // Remove event listeners
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', handleCustomStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Show loading state
  if (isLoading) {
    return (
      <Content>
        <Block>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Vérification des permissions...</span>
            </div>
            <p className="mt-3 text-soft">Vérification de vos droits d'accès...</p>
          </div>
        </Block>
      </Content>
    );
  }

  // Handle redirect for internal routes
  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  // Render route based on authorization
  // Debug log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[AdminRoute] Render state:', {
      isAuthorized,
      isLoading,
      redirectTo,
      hasComponent: !!Component,
      hasRender: !!render
    });
  }

  return (
    <Route
      exact={exact === true}
      {...rest}
      render={(props) => {
        if (isAuthorized === true) {
          // Support both component prop and render prop (like React Router)
          if (render) {
            if (process.env.NODE_ENV === 'development') {
              console.log('[AdminRoute] Rendering with render prop');
            }
            return render(props);
          }
          if (Component) {
            if (process.env.NODE_ENV === 'development') {
              console.log('[AdminRoute] Rendering with component prop');
            }
            return <Component {...props} />;
          }
          // Fallback: should not happen, but prevent crash
          console.error("[AdminRoute] Neither component nor render prop provided");
          return null;
        } else if (isAuthorized === false) {
          // Show error message if not redirected externally
          // Only show if we haven't already triggered a redirect
          if (hasRedirectedRef.current) {
            // Redirect is in progress, show loading
            return (
              <Content>
                <Block>
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Redirecting…</span>
                    </div>
                    <p className="mt-3 text-soft">Redirecting…</p>
                  </div>
                </Block>
              </Content>
            );
          }
          
          // Show error message
          return (
            <Content>
              <Head title="Access denied"></Head>
              <Block>
                <BlockHead>
                  <BlockHeadContent>
                    <BlockTitle tag="h3">Access denied</BlockTitle>
                    <BlockDes className="text-soft">
                      <p>You do not have permission to open this page.</p>
                      <p className="mt-3">
                        <a href="https://inoqare.com" className="btn btn-primary" onClick={(e) => {
                          e.preventDefault();
                          hasRedirectedRef.current = true;
                          window.location.replace("https://inoqare.com");
                        }}>
                          Go to inoqare.com
                        </a>
                      </p>
                    </BlockDes>
                  </BlockHeadContent>
                </BlockHead>
              </Block>
            </Content>
          );
        }
        // Still loading (should not happen, but just in case)
        return null;
      }}
    ></Route>
  );
};

export default AdminRoute;

